import { useMemo, type ForwardedRef } from "react";
import { UNSAFE_PortalProvider as PortalProvider } from "react-aria";
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DatePicker as DatePickerPrimitive,
  type DatePickerProps as DatePickerPrimitiveProps,
  type DateValue,
  Group,
  Heading,
  Popover,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CalendarIcon, ArrowLeftIcon, ArrowRightIcon } from "@boi/react-icons";
import { forwardRefGenerics } from "@boi/react-utils";

import * as S from "./DatePicker.styled";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, InputLabelTopV2Props } from "../InputLabelTopV2";
import { Typography } from "../Typography";
import { IconButtonV2 } from "../IconButtonV2";
import { useOverlay } from "../OverlayContext";

export type DatePickerProps<T extends DateValue> = PropsWithOptionalTestID &
  DatePickerPrimitiveProps<T> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    withCalendar?: boolean;
  };

function DatePicker<T extends DateValue>(
  props: DatePickerProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    labelProps,
    alertProps,
    shouldForceLeadingZeros = true,
    withCalendar,
    testID = "date-picker",
    ...otherProps
  } = props;

  const { tokens } = useDesignTokens();
  const { input } = tokens.datePicker;

  const alerts = useMemo(() => {
    if (alertProps) {
      return Array.isArray(alertProps) ? alertProps : [alertProps];
    }
  }, [alertProps]);

  return (
    <ReactAriaProviders
      externallyLabelledBy={props["aria-labelledby"]}
      externallyDescribedBy={props["aria-describedby"]}
      labelProps={labelProps}
      alerts={alerts}
    >
      {({ labelledBy, describedBy }) => (
        <S.Container ref={ref} data-testid={testID}>
          <InputWrapper alert={alerts && <InputAlerts alerts={alerts} />}>
            <InputLabelTopV2 {...labelProps}>
              <DatePickerPrimitive
                {...otherProps}
                aria-labelledby={labelledBy}
                aria-describedby={describedBy}
                shouldForceLeadingZeros={shouldForceLeadingZeros}
              >
                <Group>
                  <S.DateInput>
                    {(segment) => (
                      <Typography
                        component="div"
                        variant={input.segment.typography.variant}
                        fontFamily={input.segment.typography.fontFamily}
                      >
                        <S.DateSegment segment={segment} />
                      </Typography>
                    )}
                  </S.DateInput>

                  {withCalendar && (
                    <Button data-testid={`${testID}-calendar-button`}>
                      <CalendarIcon />
                    </Button>
                  )}
                </Group>

                {withCalendar && <DatePickerCalendar />}
              </DatePickerPrimitive>
            </InputLabelTopV2>
          </InputWrapper>
        </S.Container>
      )}
    </ReactAriaProviders>
  );
}

function DatePickerCalendar() {
  const { tokens } = useDesignTokens();
  const { calendar } = tokens.datePicker;

  const { portalElement } = useOverlay();

  return (
    <PortalProvider getContainer={portalElement && (() => portalElement)}>
      <Popover placement="bottom right">
        <S.Dialog>
          <S.Calendar>
            <header>
              <IconButtonV2 slot="previous">
                <ArrowLeftIcon />
              </IconButtonV2>

              <Typography
                component={Heading}
                variant={calendar.heading.typography.variant}
                fontFamily={calendar.heading.typography.fontFamily}
              />

              <IconButtonV2 slot="next">
                <ArrowRightIcon />
              </IconButtonV2>
            </header>

            <CalendarGrid>
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell>
                    <Typography
                      variant={calendar.headerCell.typography.variant}
                      fontFamily={calendar.headerCell.typography.fontFamily}
                    >
                      {day}
                    </Typography>
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>

              <CalendarGridBody>
                {(date) => (
                  <CalendarCell date={date}>
                    {({ formattedDate }) => (
                      <Typography
                        color="inherit"
                        variant={calendar.cell.typography.variant}
                        fontFamily={calendar.cell.typography.fontFamily}
                      >
                        {formattedDate}
                      </Typography>
                    )}
                  </CalendarCell>
                )}
              </CalendarGridBody>
            </CalendarGrid>
          </S.Calendar>
        </S.Dialog>
      </Popover>
    </PortalProvider>
  );
}

DatePickerCalendar.displayName = "DatePickerCalendar";

export default forwardRefGenerics(DatePicker);
